import React, { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import "./InventoryManager.css";

export default function InventoryManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [addProductError, setAddProductError] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [productView, setProductView] = useState("all");

  const emptyProduct = {
    name: "",
    description: "",
    brand: "",
    price: "",
    compare_at_price: "",
    inventory_count: "",
    category_id: "",
    subcategory_id: "",
    _thumbnailPreviewUrl: null,
    product_images: [],
    _thumbnailFile: null,
    _galleryFiles: [],
    _galleryPreviewUrls: [],
    is_active: true,
  };

  const [newProduct, setNewProduct] = useState(emptyProduct);

  // =========================
  // FETCH PRODUCTS
  // =========================
  const fetchProducts = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("hobbli_products")
        .select(`
          *,
          product_images:hobbli_product_images (
            id,
            storage_provider,
            storage_bucket,
            object_key,
            position,
            is_primary,
            created_at
          )
        `)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("FETCH ERROR:", error);
        return;
      }

      setProducts(
        (data || []).map((product) => ({
          ...product,
          product_images: [...(product.product_images || [])].sort(
            (a, b) =>
              Number(a.position || 0) - Number(b.position || 0)
          ),
        }))
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // =========================
  // PRODUCT MEDIA / R2
  // =========================
  const R2_PUBLIC_BASE_URL = "https://media.hobbli.com";

  const encodeObjectKey = (objectKey) =>
    String(objectKey)
      .split("/")
      .map((segment) => encodeURIComponent(segment))
      .join("/");

  const resolveProductImage = (image) => {
    if (!image) return null;
    if (typeof image === "string") return image;

    if (image.storage_provider === "r2" && image.object_key) {
      return `${R2_PUBLIC_BASE_URL}/${encodeObjectKey(image.object_key)}`;
    }

    return image.image_url || image.url || null;
  };

  const resolveProductThumbnail = (product) => {
    if (product?._thumbnailPreviewUrl) {
      return product._thumbnailPreviewUrl;
    }

    const images = product?.product_images || [];
    const primary =
      images.find((image) => image.is_primary) ||
      [...images].sort(
        (a, b) => Number(a.position || 0) - Number(b.position || 0)
      )[0];

    return resolveProductImage(primary);
  };

  const compressImage = (file, maxWidth = 1200, quality = 0.75) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;

        img.onload = () => {
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            const ratio = maxWidth / width;
            width = maxWidth;
            height *= ratio;
          }

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Compression failed"));
                return;
              }

              resolve(
                new File([blob], `${Date.now()}.jpg`, {
                  type: "image/jpeg",
                })
              );
            },
            "image/jpeg",
            quality
          );
        };

        img.onerror = reject;
      };

      reader.onerror = reject;
    });
  };

  const uploadProductImage = async (productId, file) => {
    if (!productId || !file) return null;

    try {
      const compressed = await compressImage(file);

      const { data: uploadAuth, error: authError } =
        await supabase.functions.invoke("create-r2-upload", {
          body: {
            contentType: compressed.type,
            fileSize: compressed.size,
            purpose: "product",
            productId,
          },
        });

      if (authError || !uploadAuth?.success || !uploadAuth?.uploadUrl) {
        console.error("R2 AUTH ERROR:", authError || uploadAuth);
        return null;
      }

      const response = await fetch(uploadAuth.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": compressed.type },
        body: compressed,
      });

      if (!response.ok) {
        console.error("R2 PUT ERROR:", response.status, await response.text());
        return null;
      }

      return {
        storage_provider: uploadAuth.storage.provider,
        storage_bucket: uploadAuth.storage.bucket,
        object_key: uploadAuth.storage.objectKey,
      };
    } catch (err) {
      console.error("PRODUCT IMAGE ERROR:", err);
      return null;
    }
  };

  const insertProductImage = async (
    productId,
    uploaded,
    { position, isPrimary = false }
  ) => {
    const { data, error } = await supabase
      .from("hobbli_product_images")
      .insert({
        product_id: productId,
        storage_provider: uploaded.storage_provider,
        storage_bucket: uploaded.storage_bucket,
        object_key: uploaded.object_key,
        position,
        is_primary: isPrimary,
      })
      .select("*")
      .single();

    if (error) {
      console.error("PRODUCT IMAGE SAVE ERROR:", error);
      return null;
    }

    return data;
  };

  const getNextImagePosition = (product) => {
    const images = product?.product_images || [];
    if (!images.length) return 0;

    return (
      Math.max(...images.map((image) => Number(image.position || 0))) + 1
    );
  };

  // =========================
  // ADD PRODUCT
  // =========================
  const addProduct = async () => {
    setAddProductError("");
    try {
      if (!newProduct.name?.trim()) {
        setAddProductError("Product name is required.");
        return;
      }

      const payload = {
        name: newProduct.name || "",
        description: newProduct.description || "",
        brand: newProduct.brand || "",
        price: Number(newProduct.price) || 0,
        compare_at_price: Number(newProduct.compare_at_price) || 0,
        inventory_count: Number(newProduct.inventory_count) || 0,
        category_id: newProduct.category_id || null,
        subcategory_id: newProduct.subcategory_id || null,
        is_active:
          newProduct.is_active === undefined ? true : newProduct.is_active,
      };

      const { data: created, error: createError } = await supabase
        .from("hobbli_products")
        .insert([payload])
        .select("*")
        .single();

      if (createError || !created) {
        console.error("ADD ERROR:", createError);
        return;
      }

      let nextPosition = 0;

      if (newProduct._thumbnailFile) {
        const thumbnail = await uploadProductImage(
          created.id,
          newProduct._thumbnailFile
        );

        if (thumbnail) {
          const savedThumbnail = await insertProductImage(
            created.id,
            thumbnail,
            { position: 0, isPrimary: true }
          );

          if (savedThumbnail) nextPosition = 1;
        }
      }

      for (const file of newProduct._galleryFiles || []) {
        const uploaded = await uploadProductImage(created.id, file);

        if (uploaded) {
          await insertProductImage(created.id, uploaded, {
            position: nextPosition,
            isPrimary: nextPosition === 0,
          });
          nextPosition += 1;
        }
      }

      setNewProduct(emptyProduct);
      setAddProductError("");
      fetchProducts();
    } catch (err) {
      console.error(err);
    }
  };

  // =========================
  // UPDATE PRODUCT
  // =========================
  const updateProduct = async (id, updated) => {
    try {
      setSavingId(id);

      const payload = {
        name: updated.name || "",
        description: updated.description || "",
        brand: updated.brand || "",
        price: Number(updated.price) || 0,
        compare_at_price:
          Number(updated.compare_at_price) || 0,
        inventory_count:
          Number(updated.inventory_count) || 0,
        category_id: updated.category_id || null,
        subcategory_id:
          updated.subcategory_id || null,
        is_active:
          updated.is_active === undefined
            ? true
            : updated.is_active,
      };

      console.log("UPDATING:", payload);

      const { error } = await supabase
        .from("hobbli_products")
        .update(payload)
        .eq("id", id);

      if (error) {
        console.error("UPDATE ERROR:", error);
        return;
      }

      fetchProducts();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingId(null);
    }
  };

  // =========================
  // HANDLE FIELD CHANGE
  // =========================
  const handleChange = (id, field, value) => {
    setProducts((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              [field]: value,
            }
          : p
      )
    );
  };

  // =========================
  // NEW PRODUCT THUMBNAIL
  // =========================
  const handleNewThumbnailUpload = (file) => {
    if (!file) return;

    setNewProduct((prev) => ({
      ...prev,
      _thumbnailFile: file,
      _thumbnailPreviewUrl: URL.createObjectURL(file),
    }));
  };

  // =========================
  // NEW PRODUCT GALLERY
  // =========================
  const handleNewGalleryUpload = (file) => {
    if (!file) return;

    setNewProduct((prev) => ({
      ...prev,
      _galleryFiles: [...(prev._galleryFiles || []), file],
      _galleryPreviewUrls: [
        ...(prev._galleryPreviewUrls || []),
        URL.createObjectURL(file),
      ],
    }));
  };

  // =========================
  // PRODUCT THUMBNAIL
  // =========================
  const handleThumbnailUpload = async (id, file) => {
    if (!file) return;

    const uploaded = await uploadProductImage(id, file);
    if (!uploaded) return;

    const product = products.find((p) => p.id === id);
    if (!product) return;

    const currentImages = product.product_images || [];
    const currentPrimary = currentImages.find((image) => image.is_primary);

    // Clear the old primary first so the partial unique index is never violated.
    if (currentPrimary) {
      const { error: clearPrimaryError } = await supabase
        .from("hobbli_product_images")
        .update({ is_primary: false })
        .eq("id", currentPrimary.id);

      if (clearPrimaryError) {
        console.error("CLEAR PRIMARY IMAGE ERROR:", clearPrimaryError);
        return;
      }
    }

    const nextPosition = getNextImagePosition(product);
    const saved = await insertProductImage(id, uploaded, {
      position: nextPosition,
      isPrimary: true,
    });

    if (!saved) return;

    await fetchProducts();
  };

  // =========================
  // PRODUCT GALLERY
  // =========================
  const handleGalleryUpload = async (id, file) => {
    if (!file) return;

    const uploaded = await uploadProductImage(id, file);
    if (!uploaded) return;

    const product = products.find((p) => p.id === id);
    if (!product) return;

    const images = product.product_images || [];
    const position = getNextImagePosition(product);

    const saved = await insertProductImage(id, uploaded, {
      position,
      isPrimary: images.length === 0,
    });

    if (!saved) return;

    await fetchProducts();
  };

  // =========================
  // REMOVE GALLERY IMAGE
  // =========================
  const removeGalleryImage = async (productId, image) => {
    if (!image?.id || image.is_primary) return;

    const { data, error } = await supabase.functions.invoke(
      "delete-r2-media",
      {
        body: {
          resourceType: "product_image",
          resourceId: image.id,
        },
      }
    );

    if (error || data?.success !== true) {
      console.error(
        "REMOVE PRODUCT IMAGE ERROR:",
        error || data?.error || data
      );
      return;
    }

    await fetchProducts();
  };

  if (loading) {
    return <div className="inventory-loading">Loading store products…</div>;
  }

  const activeCount = products.filter((product) => product.is_active).length;
  const hiddenCount = products.length - activeCount;
  const stockUnits = products.reduce((sum, product) => sum + Number(product.inventory_count || 0), 0);
  const filteredProducts = products.filter((product) => {
    const matchesView = productView === "all" || (productView === "active" ? product.is_active : !product.is_active);
    const term = productSearch.trim().toLowerCase();
    const matchesSearch = !term || [product.name, product.brand, product.description, product.id]
      .filter(Boolean).join(" ").toLowerCase().includes(term);
    return matchesView && matchesSearch;
  });

  return (
    <div className="inventory-page">
      <section className="inventory-hero">
        <div>
          <span className="inventory-eyebrow">HOBBLI OFFICIAL</span>
          <h2>Store Products</h2>
          <p>Manage Hobbli merchandise, stock, pricing, visibility, and product media.</p>
        </div>
        <button className="inventory-refresh" type="button" onClick={fetchProducts}>Refresh</button>
      </section>

      <section className="inventory-stats">
        <article><span>Total products</span><strong>{products.length}</strong><small>{activeCount} active · {hiddenCount} hidden</small></article>
        <article><span>Units in stock</span><strong>{stockUnits}</strong><small>Across all Hobbli products</small></article>
        <article><span>Active products</span><strong>{activeCount}</strong><small>Visible for sale</small></article>
        <article><span>Product images</span><strong>{products.reduce((sum, product) => sum + (product.product_images?.length || 0), 0)}</strong><small>Primary + gallery media</small></article>
      </section>

      {/* =========================
          ADD PRODUCT
      ========================= */}
      <div className="inventory-add-card">
        <h2>Add Product</h2>

        <div className="inventory-form">

          <label>Name</label>
          <input
            value={newProduct.name}
            onChange={(e) =>
              setNewProduct({
                ...newProduct,
                name: e.target.value,
              })
            }
          />

          <label>Description</label>
          <textarea
            value={newProduct.description}
            onChange={(e) =>
              setNewProduct({
                ...newProduct,
                description: e.target.value,
              })
            }
          />

          <label>Brand</label>
          <input
            value={newProduct.brand}
            onChange={(e) =>
              setNewProduct({
                ...newProduct,
                brand: e.target.value,
              })
            }
          />

          <label>Price</label>
          <input
            type="number"
            value={newProduct.price}
            onChange={(e) =>
              setNewProduct({
                ...newProduct,
                price: e.target.value,
              })
            }
          />

          <label>Compare At Price</label>
          <input
            type="number"
            value={newProduct.compare_at_price}
            onChange={(e) =>
              setNewProduct({
                ...newProduct,
                compare_at_price: e.target.value,
              })
            }
          />

          <label>Inventory Count</label>
          <input
            type="number"
            value={newProduct.inventory_count}
            onChange={(e) =>
              setNewProduct({
                ...newProduct,
                inventory_count: e.target.value,
              })
            }
          />

          <label>Thumbnail</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) =>
              handleNewThumbnailUpload(
                e.target.files[0]
              )
            }
          />

          <label>Gallery Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) =>
              handleNewGalleryUpload(
                e.target.files[0]
              )
            }
          />

          <div className="inventory-gallery">
            {(newProduct._galleryPreviewUrls || []).map(
              (img, i) => (
                <img
                  key={i}
                  src={img}
                  alt=""
                />
              )
            )}
          </div>

          <label className="inventory-checkbox">
            <input
              type="checkbox"
              checked={newProduct.is_active}
              onChange={(e) =>
                setNewProduct({
                  ...newProduct,
                  is_active: e.target.checked,
                })
              }
            />
            Active Product
          </label>


          {addProductError && (
            <div role="alert" style={{ gridColumn: "1 / -1", padding: "9px 11px", borderRadius: 8, background: "rgba(180,35,24,.10)", color: "#b42318", fontSize: ".84rem" }}>
              {addProductError}
            </div>
          )}

          <button onClick={addProduct}>
            Add Product
          </button>

        </div>
      </div>

      <section className="inventory-products-panel">
        <div className="inventory-products-heading">
          <div><h2>Product inventory</h2><p>Edit Hobbli Official merchandise without changing the seller marketplace inventory model.</p></div>
          <div className="inventory-product-tools">
            <input value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Search products…" />
            <div className="inventory-segmented">
              {[["all", "All"], ["active", "Active"], ["hidden", "Hidden"]].map(([value, label]) => (
                <button type="button" key={value} className={productView === value ? "active" : ""} onClick={() => setProductView(value)}>{label}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="inventory-grid">

        {filteredProducts.map((p) => (
          <div
            key={p.id}
            className="inventory-card"
          >

            {/* TOP */}
            <div className="inventory-card-top">

              <img
                src={
                  resolveProductThumbnail(p) ||
                  "https://via.placeholder.com/80"
                }
                className="inventory-thumb"
                alt=""
              />

              <div className="inventory-info">
                <h3>
                  {p.name || "Unnamed Product"}
                </h3>
                <p>{p.brand || "No brand"}</p>
              </div>

              <div className="inventory-meta">
                <span>
                  ${p.price || 0}
                </span>

                <div
                  className={`inventory-status ${
                    p.is_active ? "active" : "inactive"
                  }`}
                >
                  {p.is_active ? "Active" : "Hidden"}
                </div>

                <span>
                  {" "}Stock:{" "}
                  {p.inventory_count || 0}
                </span>
              </div>

            </div>

            {/* ACTIONS */}
            <div className="inventory-actions">

              <button
                onClick={() =>
                  setProducts((prev) =>
                    prev.map((x) =>
                      x.id === p.id
                        ? {
                            ...x,
                            _open: !x._open,
                          }
                        : x
                    )
                  )
                }
              >
                {p._open ? "Close" : "Edit"}
              </button>

              <button
                disabled={savingId === p.id}
                onClick={() =>
                  updateProduct(p.id, p)
                }
              >
                {savingId === p.id
                  ? "Saving..."
                  : "Save"}
              </button>

            </div>

            {/* EXPANDED */}
            {p._open && (
              <div className="inventory-expanded">

                <label className="inventory-checkbox">
                  <input
                    type="checkbox"
                    checked={p.is_active ?? true}
                    onChange={(e) =>
                      handleChange(
                        p.id,
                        "is_active",
                        e.target.checked
                      )
                    }
                  />
                  Active Product
                </label>

                <label>Name</label>
                <input
                  value={p.name || ""}
                  onChange={(e) =>
                    handleChange(
                      p.id,
                      "name",
                      e.target.value
                    )
                  }
                />

                <label>Description</label>
                <textarea
                  value={p.description || ""}
                  onChange={(e) =>
                    handleChange(
                      p.id,
                      "description",
                      e.target.value
                    )
                  }
                />

                <label>Brand</label>
                <input
                  value={p.brand || ""}
                  onChange={(e) =>
                    handleChange(
                      p.id,
                      "brand",
                      e.target.value
                    )
                  }
                />

                <label>Price</label>
                <input
                  type="number"
                  value={p.price || 0}
                  onChange={(e) =>
                    handleChange(
                      p.id,
                      "price",
                      e.target.value
                    )
                  }
                />

                <label>Inventory</label>
                <input
                  type="number"
                  value={
                    p.inventory_count || 0
                  }
                  onChange={(e) =>
                    handleChange(
                      p.id,
                      "inventory_count",
                      e.target.value
                    )
                  }
                />

                <label>Thumbnail</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleThumbnailUpload(
                      p.id,
                      e.target.files[0]
                    )
                  }
                />

                <label>Gallery Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleGalleryUpload(
                      p.id,
                      e.target.files[0]
                    )
                  }
                />

                <div className="inventory-gallery">

                  {(p.product_images || [])
                    .filter((img) => !img.is_primary)
                    .map((img) => (
                      <div
                        key={img.id}
                        className="inventory-gallery-item"
                      >

                        <img
                          src={resolveProductImage(img)}
                          alt=""
                        />

                        <button
                          onClick={() =>
                            removeGalleryImage(p.id, img)
                          }
                        >
                          Remove
                        </button>

                      </div>
                    ))}

                </div>

              </div>
            )}

          </div>
        ))}
        {!filteredProducts.length && <div className="inventory-empty">No products match this view.</div>}
        </div>
      </section>
    </div>
  );
}